import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CircularProgressBar } from "./ui/circular-progress-bar";
import { FailedTile } from "./BulkWriteItProgressContext";
import { Button } from "./ui/button";

interface BulkWriteItProgressDialogProps {
  isOpen: boolean;
  onClose: () => void;
  progress: number; // 0-100
  statusMessage: string;
  failedTiles: FailedTile[];
}

export const BulkWriteItProgressDialog: React.FC<BulkWriteItProgressDialogProps> = ({
  isOpen,
  onClose,
  progress,
  statusMessage,
  failedTiles,
}) => {
  const isComplete = progress === 100;
  const hasFailedTiles = failedTiles.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]" showCloseButton={isComplete}>
        <DialogHeader>
          <DialogTitle>{isComplete ? "Bulk Write It Results" : "Bulk Write It Progress"}</DialogTitle>
          {!(isComplete && hasFailedTiles) && <DialogDescription>{statusMessage}</DialogDescription>}
        </DialogHeader>

        {!isComplete || (isComplete && !hasFailedTiles) ? (
          <div className="flex justify-center py-4">
            <CircularProgressBar progress={progress} size={150} />
          </div>
        ) : null}

        {isComplete && !hasFailedTiles && (
          <div className="flex justify-center">
            <Button onClick={onClose} variant="default">
              Done
            </Button>
          </div>
        )}

        {isComplete && hasFailedTiles && (
          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-semibold">Failed Tiles:</h3>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {failedTiles.map((tile) => (
                <div
                  key={tile.id}
                  className="border-s-4 border-danger-400 bg-danger-200/30 p-2 rounded-md"
                  role="alert"
                >
                  <div className="flex items-start">
                    <div className="shrink-0">
                      <span className="inline-flex justify-center items-center size-6 rounded-full border-4 border-danger-200/50 bg-danger-200 text-danger-400">
                        <svg
                          className="shrink-0 size-3"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 6 6 18"></path>
                          <path d="m6 6 12 12"></path>
                        </svg>
                      </span>
                    </div>
                    <div className="ms-3">
                      <h3 className="text-sm font-semibold">{tile.text}</h3>
                      <p className="text-xs text-neutral-400">{tile.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
